import storageService from './storageService';
import {
    COMPAT_KEYS,
    OIDC_SESSION_PREFIX,
    STORAGE_BACKENDS,
    STORAGE_DOMAINS,
    STORAGE_KEYS
} from './storageKeys';
import { resolveSsoPayload } from './ssoProviders';

const defaultAuthSession = {
    isLoggedIn: false,
    userName: null,
    authMethod: null,
    providerId: null,
    issuedAt: null,
    schemaVersion: 1
};

const isBrowser = typeof window !== 'undefined';

const hasCookie = (cookieName) => {
    if (!isBrowser || !cookieName) {
        return false;
    }

    return document.cookie
        .split(';')
        .map((cookie) => cookie.trim())
        .some((cookie) => cookie.startsWith(`${cookieName}=`));
};

const buildAuthSession = (authSession = {}) => ({
    ...defaultAuthSession,
    ...authSession,
    isLoggedIn: Boolean(authSession.isLoggedIn),
    userName: authSession.userName || null,
    issuedAt: authSession.issuedAt || Date.now(),
    schemaVersion: 1
});

export const getAuthSession = () => {
    const namespacedSession = storageService.getJSON(STORAGE_KEYS.authSession, null);
    if (namespacedSession && typeof namespacedSession === 'object') {
        const normalizedSession = buildAuthSession(namespacedSession);
        if (normalizedSession.isLoggedIn || !isBrowser) {
            return normalizedSession;
        }
    }

    if (!isBrowser) {
        return defaultAuthSession;
    }

    const isLoggedIn = window.localStorage.getItem(COMPAT_KEYS.IS_LOGGED_IN) === 'true';
    const userName = window.localStorage.getItem(COMPAT_KEYS.USER_NAME);

    if (!isLoggedIn) {
        if (hasCookie('PHPSESSID')) {
            const cookieSession = buildAuthSession({
                isLoggedIn: true,
                authMethod: 'cookie',
                providerId: 'php-session'
            });
            storageService.setJSON(STORAGE_KEYS.authSession, cookieSession);
            return cookieSession;
        }

        return defaultAuthSession;
    }

    const migratedSession = buildAuthSession({
        isLoggedIn,
        userName,
        authMethod: 'compat',
        providerId: 'compat'
    });
    storageService.setJSON(STORAGE_KEYS.authSession, migratedSession);

    return migratedSession;
};

export const setAuthSession = ({
    isLoggedIn,
    userName,
    authMethod = 'password',
    providerId = 'local'
}) => {
    const nextSession = buildAuthSession({ isLoggedIn, userName, authMethod, providerId });
    storageService.setJSON(STORAGE_KEYS.authSession, nextSession);

    if (nextSession.isLoggedIn) {
        storageService.writeRaw(
            COMPAT_KEYS.IS_LOGGED_IN,
            'true',
            STORAGE_BACKENDS.LOCAL,
            STORAGE_DOMAINS.AUTH
        );

        if (nextSession.userName) {
            storageService.writeRaw(
                COMPAT_KEYS.USER_NAME,
                nextSession.userName,
                STORAGE_BACKENDS.LOCAL,
                STORAGE_DOMAINS.AUTH
            );
        }
    } else {
        storageService.removeRaw(COMPAT_KEYS.IS_LOGGED_IN, STORAGE_BACKENDS.LOCAL, STORAGE_DOMAINS.AUTH);
        storageService.removeRaw(COMPAT_KEYS.USER_NAME, STORAGE_BACKENDS.LOCAL, STORAGE_DOMAINS.AUTH);
    }

    return nextSession;
};

export const cacheUserPayload = (userName, payload) => {
    if (!userName) {
        return;
    }

    const key = `${COMPAT_KEYS.USER_PREFIX}${userName}`;
    storageService.writeRaw(key, JSON.stringify(payload || {}), STORAGE_BACKENDS.LOCAL, STORAGE_DOMAINS.USER);
};

export const clearAuthAndSessionState = ({ clearOidcKeys = true } = {}) => {
    storageService.clearDomain(STORAGE_DOMAINS.AUTH, STORAGE_BACKENDS.LOCAL);
    storageService.clearDomain(STORAGE_DOMAINS.SSO, STORAGE_BACKENDS.SESSION);

    storageService.removeRaw(COMPAT_KEYS.IS_LOGGED_IN, STORAGE_BACKENDS.LOCAL, STORAGE_DOMAINS.AUTH);
    storageService.removeRaw(COMPAT_KEYS.USER_NAME, STORAGE_BACKENDS.LOCAL, STORAGE_DOMAINS.AUTH);
    storageService.removeRaw(COMPAT_KEYS.SSO_IN_FLIGHT, STORAGE_BACKENDS.SESSION, STORAGE_DOMAINS.SSO);

    if (isBrowser) {
        const localKeys = Object.keys(window.localStorage);
        localKeys.forEach((key) => {
            if (key.startsWith(COMPAT_KEYS.USER_PREFIX)) {
                storageService.removeRaw(key, STORAGE_BACKENDS.LOCAL, STORAGE_DOMAINS.USER);
            }
        });

        if (clearOidcKeys) {
            const sessionKeys = Object.keys(window.sessionStorage);
            sessionKeys.forEach((key) => {
                if (key.startsWith(OIDC_SESSION_PREFIX)) {
                    storageService.removeRaw(key, STORAGE_BACKENDS.SESSION, STORAGE_DOMAINS.SSO);
                }
            });
        }
    }

    storageService.setJSON(STORAGE_KEYS.authSession, { ...defaultAuthSession });
};

export const isLoggedIn = () => getAuthSession().isLoggedIn;

export const getUserName = () => getAuthSession().userName;

export const setSsoInFlight = (value) => {
    if (value) {
        storageService.writeRaw(
            COMPAT_KEYS.SSO_IN_FLIGHT,
            'true',
            STORAGE_BACKENDS.SESSION,
            STORAGE_DOMAINS.SSO
        );
        storageService.setJSON(STORAGE_KEYS.ssoInFlight, { inFlight: true });
    } else {
        storageService.removeRaw(COMPAT_KEYS.SSO_IN_FLIGHT, STORAGE_BACKENDS.SESSION, STORAGE_DOMAINS.SSO);
        storageService.removeKey(STORAGE_KEYS.ssoInFlight);
    }
};

export const getSsoInFlight = () => {
    const namespacedValue = storageService.getJSON(STORAGE_KEYS.ssoInFlight, null);
    if (namespacedValue) {
        return Boolean(namespacedValue.inFlight);
    }

    if (!isBrowser) {
        return false;
    }

    return window.sessionStorage.getItem(COMPAT_KEYS.SSO_IN_FLIGHT) === 'true';
};

export const resolveSsoLoginData = () => {
    if (!isBrowser) {
        return null;
    }

    return resolveSsoPayload(window.sessionStorage);
};
